/** 
 * Custom hook for managing chat streaming. It's on the client-side because we already have auth on the client side.
 * We are using SSE (Server-sent events) for streaming events to keep the connection open.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { setAnalysisResults, clearAnalysisResults, clearUploadedFiles, ensureSessionId as ensureSessionIdAction, markConversationStarted, incrementSentFileCounts } from '../store/chatSlice';
import { hideGlobalBlocking } from '../store/loadingSlice';
import { setIsStreaming } from '../store/chatModeSlice';
import { resolveModelConfig, MODEL_OPTIONS, ModelConfig, WEB_SEARCH_MODEL_ID } from '../utils/config/modelConfig';
import { useMsal } from '@azure/msal-react';
import { ChatService, ChatMessage, ChatContentItem, createChatService } from '../utils/chat/chat.service';
import logger from '../utils/logger/logger';
import { utf8ToBase64 } from '../utils/base64.utils';
import { parseWebSearchStream } from '../utils/stream/parseWebSearchStream';

/** Map a model id to its user-facing display name. Returns the id itself as fallback. */
const getModelDisplayName = (id: string | undefined | null): string | undefined => {
  if (!id) return undefined;
  const found = MODEL_OPTIONS.find((m: ModelConfig) => m.id === id);
  return found ? found.displayName : id;
};

/**
 * Quick sanitization to prevent HTML/script injection in user input
 * Escapes dangerous tags while preserving the text content
 */
const sanitizeUserInput = (text: string): string => {
  return text
    // Escape opening angle brackets for dangerous tags
    .replace(/<script/gi, '&lt;script')
    .replace(/<\/script/gi, '&lt;/script')
    .replace(/<iframe/gi, '&lt;iframe')
    .replace(/<\/iframe/gi, '&lt;/iframe')
    .replace(/<object/gi, '&lt;object')
    .replace(/<embed/gi, '&lt;embed')
    .replace(/<link/gi, '&lt;link')
    .replace(/<style/gi, '&lt;style')
    .replace(/<form/gi, '&lt;form')
    // Escape div/span with ref/onclick/onload attributes
    .replace(/<(div|span)([^>]*?(ref|onclick|onload|onerror|onmouseover)[^>]*?)>/gi, '&lt;$1$2&gt;')
    // Escape any remaining standalone angle brackets that could form tags
    .replace(/<([a-z][a-z0-9]*)\s/gi, '&lt;$1 ');
};

export interface UseChatStreamOptions {
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface ChatStreamState {
  isStreaming: boolean;
  error: Error | null;
  currentResponse: string;
  chatHistory: ChatMessage[];
  currentTools?: string[]; // Track current request tools
  isImageStreaming?: boolean; // Track specifically if image is streaming
  reasoningContent?: string; // Live accumulated reasoning (hidden behind dropdown until expanded)
  reasoningActive?: boolean; // Whether current stream uses the reasoning tool
  thinkingContent?: string; // Web search thinking trace (for dropdown)
  thinkingComplete?: boolean; // Whether thinking section has finished streaming
  webSearchJustCompleted?: boolean; // Track if we just completed a web search (for spacing)
  webSearchCitations?: Array<{url: string; title: string; snippet?: string}>; // Web search citations for SourcesList
  // When set, backend indicated we should switch to Legal (la) or HR (hr) RAG.
  ragSwitchSuggestion?: { message: string; ragType: 'la' | 'hr' } | null;
  // Debug info
  debugInfo: {
    apiRequestTime: string | null;
    requestPayload: ChatRequestPayload | null;
    firstChunkTime: string | null;
    lastChunkTime: string | null;
    totalChunks: number;
    contentChunks: number;
    queueLength: number;
    isProcessingQueue: boolean;
    lastUpdate?: number;
    forceUpdate?: number;
  };
}

interface ChatRequestPayload {
  request_id: string;
  session: string; // newly added per-session identifier
  timestamp: string;
  metadata: {
    source: string;
  };
  query_type: string;
  chat_history: ChatMessage[];
  tools?: string[];
}

type LegacyRequestFile = { filename: string; content_base64?: string; file_id?: string };

type SendMessageOptions = {
  continueLlmResponse?: boolean;
  overrideHistory?: ChatMessage[];
  overrideRequestFiles?: LegacyRequestFile[];
  detectRagSwitch?: boolean;
  ragType?: 'la' | 'hr';
};

const LA_SWITCH_MARKER = 'SWITCH_TO_LA_RAG';
const HR_SWITCH_MARKER = 'SWITCH_TO_HR_RAG';

export const useChatStream = ({ onError, onComplete }: UseChatStreamOptions) => {
  const { instance: msalInstance } = useMsal();
  
  const hasLoggedFirstContentRef = useRef(false);
  const currentResponseRef = useRef<string>('');
  const forceUpdateRef = useRef(0);
  const imageStreamingCompleteRef = useRef(false);
  const completionHandledRef = useRef(false); // Global completion flag
  const isStreamingRef = useRef(false); // Always-current ref to avoid stale closure in sendMessage guard
  // Timestamp of the last processed chunk — updated by processQueue on every drain.
  // Used by the idle-based safety timeout to detect truly stalled streams vs healthy long runs.
  const lastChunkTimeRef = useRef<number>(0);
  // Whether the current in-flight request included uploaded files (affects idle timeout tier).
  const requestHasFilesRef = useRef<boolean>(false);
  const finalContentRef = useRef<string>(''); // Preserve final content for fallback validation
  const completedMessageRef = useRef<ChatMessage | null>(null); // Track completed message to prevent loss
  const currentResponseIdRef = useRef<string | null>(null); // Track backend response ID for session continuity
  const analysisCodeBlockOpenRef = useRef(false); // Track code block state for data_analysis events
  const analysisCodeBufferRef = useRef<string>(''); // Accumulates code until block end
  // Accumulate follow-up suggestions during streaming
  const followUpsRef = useRef<string[]>([]);
  // Map containers to discovered file ids for data_analysis; we defer UI output until we have both
  // Track container -> files map with metadata so we can render download links later
  const analysisContainersRef = useRef<Map<string, { files: Map<string, { displayName: string }> }>>(new Map());
  // Placeholder sandbox links captured before we have file ids
  // Removed placeholderFilesRef (legacy sandbox placeholder tracking)
  // Track enriched placeholders (including original line & resolved status)
  // Removed enrichedPlaceholdersRef (deprecated after post-completion transformation pivot)
  const lastContainerIdRef = useRef<string | null>(null);
  // Web search citations tracking
  const webSearchCitationsRef = useRef<Array<{url: string; title: string; snippet?: string}>>([]);
  // Use global persistent session id from Redux
  const sessionId = useSelector((s: RootState) => s.chat.sessionId);
  const selectedModelId = useSelector((s: RootState) => s.chatMode.selectedModelId);
  // Keep a ref always in sync with selectedModelId so the sendMessage callback (which has a
  // stable deps array for performance) never captures a stale model value.
  // CRITICAL: Assign synchronously during render (not via useEffect) to prevent stale reads
  // when user switches models and sends a message in the same tick.
  const selectedModelIdRef = useRef(selectedModelId);
  selectedModelIdRef.current = selectedModelId;
  // Trigger state to allow effects to react to completion (refs don't trigger rerenders)
  const [completionTrigger, setCompletionTrigger] = useState(0);
  
  const [state, setState] = useState<ChatStreamState>({
    isStreaming: false,
    error: null,
    currentResponse: '',
    chatHistory: [],
    currentTools: undefined,
    isImageStreaming: false,
    reasoningContent: '',
    reasoningActive: false,
    thinkingContent: '', // Web search thinking trace
    thinkingComplete: false, // Whether thinking is done
    webSearchJustCompleted: false, // Track if we just completed a web search (for spacing)
    ragSwitchSuggestion: null,
    debugInfo: {
      apiRequestTime: null,
      requestPayload: null,
      firstChunkTime: null,
      lastChunkTime: null,
      totalChunks: 0,
      contentChunks: 0,
      queueLength: 0,
      isProcessingQueue: false,
      lastUpdate: 0,
      forceUpdate: 0
    }
  });

  const legalSwitchRequestRef = useRef<{
    message: string;
    tools?: string[];
    effectiveHistory: ChatMessage[];
    requestFiles?: LegacyRequestFile[];
    ragType: 'la' | 'hr';
  } | null>(null);

  // CRITICAL FIX: Use ref to track latest chatHistory to avoid stale closures
  // This ensures sendMessage always has access to the current history
  const chatHistoryRef = useRef<ChatMessage[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    chatHistoryRef.current = state.chatHistory;
  }, [state.chatHistory]);

  // Keep isStreamingRef in sync so sendMessage guard never reads a stale closure value
  useEffect(() => {
    isStreamingRef.current = state.isStreaming;
  }, [state.isStreaming]);

  // Keep a ref to the chat service
  const chatServiceRef = useRef<ChatService | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Uploaded files from Redux for data analysis
  const uploadedFilesState = useSelector((s: RootState) => s.chat.uploadedFiles);
  const dispatch = useDispatch();

  // Initialize chat service object
  const getChatService = useCallback(() => {
    if (!chatServiceRef.current) {
      chatServiceRef.current = createChatService(msalInstance);
    }
    return chatServiceRef.current;
  }, [msalInstance]);

  // Ensure session id exists (after dispatch is defined)
  useEffect(() => {
    if (!sessionId) {
      dispatch(ensureSessionIdAction());
    }
  }, [sessionId, dispatch]);

  // Sync local isStreaming state to Redux synchronously so Header guards are always current
  useEffect(() => {
    dispatch(setIsStreaming(state.isStreaming));
  }, [state.isStreaming, dispatch]);

  // Send a message and stream the response
  const sendMessage = useCallback(async (message: string, tools?: string[], options?: SendMessageOptions) => {
    // shouldn't happen but just in case
    if (isStreamingRef.current) {
      console.warn('Already streaming, ignoring new message');
      return;
    }

    // // For data analysis: regenerate session ID ONLY on the first data analysis request
    // // This ensures a fresh OpenAI session while allowing follow-up questions in the same session
    // const isDataAnalysis = tools?.includes('data_analysis');
    // if (isDataAnalysis) {
    //   const hasExistingDataAnalysis = state.chatHistory.some(msg => 
    //     msg.tools?.includes('data_analysis')
    //   );
      
    //   if (!hasExistingDataAnalysis) {
    //     logger.log('[DATA-ANALYSIS] First data analysis request - regenerating session ID for fresh OpenAI session');
    //     dispatch(regenerateSessionId());
    //   } else {
    //     logger.log('[DATA-ANALYSIS] Follow-up request - keeping existing session for context');
    //   }
    // }

    // Reset flags for new message
    currentResponseRef.current = '';
    currentResponseIdRef.current = null; // Clear previous response ID
    imageStreamingCompleteRef.current = false;
    webSearchCitationsRef.current = []; // Clear web search citations
    completedMessageRef.current = null; // CRITICAL: Clear old completed message ref
    completionHandledRef.current = false; // Reset completion flag for new stream
    finalContentRef.current = ''; // Clear final content from previous request
    // Queue and heading refs will be defined later; provide deferred helper placeholder.
    let ensureHeadingFirst: () => void = () => { /* initialized below after chunkQueue declared */ };
    imageStreamingCompleteRef.current = false;

    const shouldDetectRagSwitch = options?.detectRagSwitch !== false;
    const overrideHistory = options?.overrideHistory;

    // Create the full chat history for the request first
    // Tag this user message with current selected tools (if any). If there are locally uploaded files,
    // construct structured content array per new spec (unless it's data_analysis which has special handling).
    const isDataAnalysis = tools?.includes('data_analysis');
    const isImageGen = tools?.includes('image_generation');
    let taggedUserMessage: ChatMessage | null = null;
    
    // If caller provided overrideHistory, do not append a new user message.
    // This is used for retries where the user message already exists in state.
    if (overrideHistory) {
      taggedUserMessage = null;
    }
    // Include uploaded files in structured content for UI rendering.
    // For data_analysis we only keep filenames in chat history and continue sending file_ids separately.
    else if (uploadedFilesState.length > 0) {
      const structured = [
        ...uploadedFilesState.map(f => {
          const looksBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(f.data.substring(0,64));
          let base64 = f.data;
          if (!looksBase64 && f.type === 'text/csv') {
            try { base64 = utf8ToBase64(f.data); } catch { base64 = btoa(f.data); }
          }

          // Check if this is an image file (png, jpeg, jpg)
          const isImage = /image\/(png|jpeg|jpg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name);
          
          if (isImage) {
            // Images use input_image type with image_url property
            const imageMime = f.type === 'image/png' ? 'image/png' : 'image/jpeg';
            return { type: 'input_image', image_url: `data:${imageMime};base64,${base64}` } as ChatContentItem;
          }

          // Non-image files use input_file type with file_data property
          const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
          const mime = isPdf ? 'application/pdf' : (f.type || 'application/octet-stream');
          return { type: 'input_file', filename: f.name, file_data: `data:${mime};base64,${base64}` } as ChatContentItem;
        }),
        { type: 'input_text', text: sanitizeUserInput(message) } as ChatContentItem
      ];
      taggedUserMessage = { id: `${Date.now()}-${Math.random()}`, role: 'user', content: structured, tools };
    } else {
      taggedUserMessage = { id: `${Date.now()}-${Math.random()}`, role: 'user', content: sanitizeUserInput(message), tools };
    }
    
    // Use ref to get latest chatHistory (avoids stale closure)
    const cleanHistory = chatHistoryRef.current;
    
    const fullHistory = overrideHistory
      ? overrideHistory
      : [
          ...cleanHistory,
          taggedUserMessage as ChatMessage
        ];

    // Filter chat history based on current tool selection
    const isImageGeneration = tools?.includes('image_generation');
    const filteredHistory = fullHistory.map((msg) => {
      // Strip file_data from historical messages (not the current taggedUserMessage).
      // The API expects file_id for previously-processed files in history; re-sending
      // large base64 file_data for prior turns causes a 400 validation error:
      // "Missing mutually exclusive parameters: provide exactly one of file_id or image_url"
      let processedMsg = msg;
      const shouldStripEmbeddedData = Array.isArray(msg.content)
        && (msg !== taggedUserMessage || isDataAnalysis);
      if (shouldStripEmbeddedData) {
        const strippedContent = (msg.content as any[]).map((item: any) => {
          if (item.type === 'input_file' && item.file_data) {
            const stripped = { ...item };
            delete (stripped as any).file_data;
            return stripped;
          }
          if (item.type === 'input_image' && item.image_url) {
            const stripped = { ...item };
            delete (stripped as any).image_url;
            return stripped;
          }
          return item;
        });
        processedMsg = { ...msg, content: strippedContent };
      }

      // Check if this is an assistant message with an image
      const isAssistantImage = processedMsg.role === 'assistant' && 
                               typeof processedMsg.content === 'string' && 
                               processedMsg.content.includes('![Generated Image]');
      
      if (isImageGeneration && isAssistantImage) {
        // For image generation requests: skip previous assistant image messages entirely
        return null;
      } else if (!isImageGeneration && isAssistantImage) {
        // For non-image requests: replace assistant image content with generic message
        return {
          ...processedMsg,
          content: 'Assistant generated an image.'
        };
      }
      
      return processedMsg;
    }).filter((msg): msg is ChatMessage => msg !== null); // Remove null entries (skipped images)

    // Immediately add user message to chat history for instant display (unless override mode)
    if (!overrideHistory) {
      setState(prev => ({
        ...prev,
        chatHistory: fullHistory
      }));
    }

    // NOTE: Files are now kept in state to remain visible in the chat input
    // They are only cleared when user explicitly removes them or clears chat
    // This ensures consistent behavior across all tools (like data_analysis)

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Capture the request payload for debugging
    const requestPayload: ChatRequestPayload = {
      request_id: `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
  session: sessionId || 'pending_session',
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'react-app'
      },
      query_type: 'response-api',
      chat_history: filteredHistory // Use filtered history instead of fullHistory
    };

  // (headingInsertedRef will be defined after chunkQueue declaration; reset deferred via local flag)
  let headingResetNeeded = true;
    hasLoggedFirstContentRef.current = false;
    completionHandledRef.current = false; // Reset completion flag for new stream
    followUpsRef.current = []; // Reset follow-ups for new stream
    requestHasFilesRef.current = uploadedFilesState.length > 0; // Track if request has files for timeout
    
  const apiRequestTime = new Date().toISOString();
  // Dispatch synchronously so Redux (and Header) reflect streaming=true before the next render
  dispatch(setIsStreaming(true));
  setState(prev => ({
      ...prev,
      isStreaming: true,
      error: null,
      ragSwitchSuggestion: null,
      currentResponse: '', // Never set static placeholder, ReasoningDropdown handles loading UI
      currentTools: tools, // Track the tools for this request
      isImageStreaming: tools?.includes('image_generation') || false,
      reasoningActive: !!tools?.includes('reasoning'),
      reasoningContent: '',
      thinkingContent: '', // Reset thinking for new request
      thinkingComplete: false, // Reset thinking complete flag
      debugInfo: {
        apiRequestTime,
        requestPayload,
        firstChunkTime: null,
        lastChunkTime: null,
        totalChunks: 0,
        contentChunks: 0,
        queueLength: 0,
        isProcessingQueue: false
      }
    }));

  const chatService = getChatService();
    let accumulatedResponse = '';
    const chunkQueue: string[] = []; // Queue to hold content chunks
    // Reasoning and web-search streaming now handled by parser utilities (see parseReasoningStream)
    let reasoningBuffer = ''; // Accumulates streaming reasoning/chain-of-thought content
    let ragSwitchDetectionActive = shouldDetectRagSwitch;
    let ragSwitchBuffer = '';
  // Track last full replacement snapshot to avoid emitting identical replacements repeatedly
  const lastReplacementSnapshotRef = { current: '' };
  // Prevent multiple concurrent waitForQueueCompletion executions
  let isWaitingForCompletion = false;
  // Serialization guards for processQueue to prevent concurrent drains.
  // Replaces the isProcessingQueue pattern that called processQueue from inside
  // setState updaters — which caused React's "Maximum update depth exceeded" in reasoning mode.
  let isQueueProcessing = false;
  let isQueueDrainScheduled = false;
  // Track the last image chunk we received to prevent duplicate queueing
  let lastImageChunkHash = '';
  // Track whether we have already inserted the output heading to prevent duplicates on repeated container events
  const headingInsertedRef = { current: headingResetNeeded ? false : false };
  headingResetNeeded = false;

    // Now that chunkQueue & heading ref exist, assign real implementation.
    // Used later inside handleAnalysisEvent for container/file_citation events
    ensureHeadingFirst = () => {
      if (headingInsertedRef.current) return;
      headingInsertedRef.current = true;
      try {
        const pendingCombined = currentResponseRef.current + chunkQueue.join('');
        const heading = `\n**Outputs created for you:**\n`;
        const newContent = heading + pendingCombined;
        currentResponseRef.current = newContent;
        chunkQueue.length = 0;
        chunkQueue.push('[__REPLACE__]' + newContent.replace(/\n/g,'  \n'));
        setState(prev => ({
          ...prev,
          debugInfo: {
            ...prev.debugInfo,
            contentChunks: prev.debugInfo.contentChunks + 1,
            queueLength: chunkQueue.length
          }
        }));
      } catch {/* ignore prepend errors */}
    };

    // Merged debug-only state update — skips re-render when values haven't changed.
    const updateQueueDebugInfo = (updates: Partial<ChatStreamState['debugInfo']>) => {
      setState(prev => {
        const d = prev.debugInfo;
        const changed = (Object.keys(updates) as Array<keyof ChatStreamState['debugInfo']>)
          .some(k => d[k] !== (updates as any)[k]);
        if (!changed) return prev;
        return { ...prev, debugInfo: { ...d, ...updates } };
      });
    };

    const finishQueueProcessing = () => {
      isQueueProcessing = false;
      if (completionHandledRef.current) {
        chunkQueue.length = 0;
        updateQueueDebugInfo({ isProcessingQueue: false, queueLength: 0 });
        return;
      }
      if (chunkQueue.length > 0) {
        scheduleProcessQueue();
        return;
      }
      updateQueueDebugInfo({ isProcessingQueue: false, queueLength: 0 });
    };

    // scheduleProcessQueue and processQueue are mutually recursive — declare before assignment.
    let scheduleProcessQueue: () => void = () => {};

    // Process queue with delays to create streaming effect
    const processQueue = () => {
      // If completion already handled, stop further queue processing
      if (completionHandledRef.current) {
        // Clear queue to avoid residual processing
        chunkQueue.length = 0;
        isQueueProcessing = false;
        updateQueueDebugInfo({ isProcessingQueue: false, queueLength: 0 });
        return;
      }

      // Prevent concurrent executions — new chunks arriving mid-drain are picked up
      // by finishQueueProcessing → scheduleProcessQueue once the current drain ends.
      if (isQueueProcessing) return;

      if (chunkQueue.length === 0) {
        // Don't update state if completion already handled (prevent overwriting completed message)
        if (!completionHandledRef.current) {
          setState(prev => {
            // CRITICAL: Double-check completion hasn't happened since we entered this function
            // If a completed message exists, always merge it to prevent loss
            const completedMsg = completedMessageRef.current;
            const needsMerge = completedMsg && !prev.chatHistory.find(m => m.id === completedMsg.id);
            
            // CRITICAL: If we're merging the completed message, set completion flag
            // to prevent waitForQueueCompletion from adding it again
            if (needsMerge) {
              completionHandledRef.current = true;
              isWaitingForCompletion = false; // Reset waiting flag
            }
            
            return {
              ...prev,
              chatHistory: needsMerge ? [...prev.chatHistory, completedMsg] : prev.chatHistory,
              // If we're merging a completed message, also ensure streaming is stopped
              isStreaming: needsMerge ? false : prev.isStreaming,
              isImageStreaming: needsMerge ? false : prev.isImageStreaming,
              currentResponse: needsMerge ? '' : prev.currentResponse,
              currentTools: needsMerge ? undefined : prev.currentTools,
              debugInfo: {
                ...prev.debugInfo,
                isProcessingQueue: false,
                queueLength: 0
              }
            };
          });
        }
        isQueueProcessing = false;
        return;
      }

      isQueueProcessing = true;

      // Don't update state if completion already handled
      if (completionHandledRef.current) {
        isQueueProcessing = false;
        return;
      }

      try {
      updateQueueDebugInfo({
        isProcessingQueue: true,
        queueLength: chunkQueue.length
      });

      const nextChunk = chunkQueue.shift()!;
      
      // INTERNAL REPLACEMENT SENTINEL (analysis only): [__REPLACE__] prefix means overwrite accumulatedResponse fully
      if (nextChunk.startsWith('[__REPLACE__]')) {
        const replacementBody = nextChunk.slice('[__REPLACE__]'.length);
        // Skip if identical to last replacement (prevents visible duplication)
        if (replacementBody === lastReplacementSnapshotRef.current) {
          // Content is identical — skip render but continue draining
          finishQueueProcessing();
          return;
        }
        accumulatedResponse = replacementBody;
        lastReplacementSnapshotRef.current = replacementBody;
        // Don't update state if completion already handled
        if (!completionHandledRef.current) {
          // Use flushSync for immediate UI update without batching delay
          flushSync(() => {
            setState(prev => ({
              ...prev,
              currentResponse: accumulatedResponse,
              debugInfo: { ...prev.debugInfo, queueLength: chunkQueue.length, lastUpdate: Date.now() }
            }));
          });
        }
        currentResponseRef.current = accumulatedResponse;
        finishQueueProcessing();
        return;
      }

      // Check if this is a replacement message from the chat service
      try {
        const parsedChunk = JSON.parse(nextChunk);
        if (parsedChunk.type === 'replace' && parsedChunk.content) {
          accumulatedResponse = parsedChunk.content;
        } else {
          // Regular content, accumulate as normal
          accumulatedResponse += nextChunk;
        }
      } catch {
        // Not JSON, handle as regular content
        // Handle image content specially - replace instead of accumulate
        if (nextChunk.includes('![Generated Image](data:image/png;base64,')) {
          // CRITICAL: Check if accumulatedResponse already contains an image marker
          // If it does, this is a duplicate/update chunk - replace the entire content
          // If it doesn't, this is the first image chunk - use it directly
          const alreadyHasImage = accumulatedResponse.includes('![Generated Image](data:image/png;base64,');
          if (alreadyHasImage) {
            // Replace existing image content with new chunk (it's an update/progress chunk)
            accumulatedResponse = nextChunk;
          } else {
            // First image chunk - could have preceding text, so check if we should replace or append
            // For pure image generation, typically no preceding text, so replace is safe
            accumulatedResponse = nextChunk;
          }
        } else {
          // For web_search / reasoning: replace the loading placeholder with the first
          // real content chunk. Use an explicit flag rather than string-comparing
          // accumulatedResponse, which is unreliable if any chunk leaked through early.
          // For text content, accumulate as normal
          // ONLY add space if we just completed a web search AND there's existing content without trailing whitespace
          const needsSpaceAfterWebSearch = 
            state.webSearchJustCompleted === true && 
            accumulatedResponse.length > 0 && 
            !/\s$/.test(accumulatedResponse);
          
          accumulatedResponse += (needsSpaceAfterWebSearch ? ' ' : '') + nextChunk;
        }
      }
      
      // Don't update state if completion already handled
      if (!completionHandledRef.current) {
        // Use flushSync for immediate, non-batched UI updates - critical for smooth streaming
        flushSync(() => {
          setState(prev => {
            // Check if we need to merge a completed message that was created concurrently
            const completedMsg = completedMessageRef.current;
            const needsMerge = completedMsg && !prev.chatHistory.find(m => m.id === completedMsg.id);
            
            return {
              ...prev,
              chatHistory: needsMerge ? [...prev.chatHistory, completedMsg] : prev.chatHistory,
              // If we're merging a completed message, also ensure streaming is stopped
              isStreaming: needsMerge ? false : prev.isStreaming,
              isImageStreaming: needsMerge ? false : prev.isImageStreaming,
              currentResponse: needsMerge ? '' : accumulatedResponse,
              currentTools: needsMerge ? undefined : prev.currentTools,
              webSearchJustCompleted: false, // Reset flag after using it
              debugInfo: {
                ...prev.debugInfo,
                queueLength: chunkQueue.length,
                lastUpdate: Date.now()
              }
            };
          });
        });
      }

      // Update ref with current response for immediate access
      currentResponseRef.current = accumulatedResponse;
      // Record activity time so the idle-based safety timeout can distinguish a
      // healthy long-running stream from a genuinely stalled one.
      lastChunkTimeRef.current = Date.now();

      // Schedule processing of the next chunk (or mark queue idle if empty)
      finishQueueProcessing();
      } catch (err) {
        // Ensure isQueueProcessing is always reset so the queue never stalls permanently
        console.error('[useChatStream] processQueue threw unexpectedly:', err);
        isQueueProcessing = false;
        isQueueDrainScheduled = false;
        updateQueueDebugInfo({ isProcessingQueue: false });
      }
    };

    // Coalesces concurrent scheduling requests so only one rAF drain runs at a time.
    // This is the core fix for React's "Maximum update depth exceeded" in reasoning mode:
    // rapid SSE chunks previously each spawned a new setTimeout(processQueue, 0) inside
    // a setState updater, causing re-entrant nested update loops.
    scheduleProcessQueue = () => {
      if (completionHandledRef.current || isQueueProcessing || isQueueDrainScheduled || chunkQueue.length === 0) {
        return;
      }
      isQueueDrainScheduled = true;
      requestAnimationFrame(() => {
        isQueueDrainScheduled = false;
        if (completionHandledRef.current || isQueueProcessing) return;
        if (chunkQueue.length === 0) {
          updateQueueDebugInfo({ isProcessingQueue: false, queueLength: 0 });
          return;
        }
        processQueue();
      });
    };

    try {
      // For image generation requests, only send the latest user message (no history)
      // Use filteredHistory (not fullHistory) to respect image filtering logic
      const effectiveHistory = tools?.includes('image_generation')
        ? filteredHistory.slice(-1) // Only the latest message (the user message we just added)
        : filteredHistory;

      logger.log('[SEND-MESSAGE][DISPATCH]', {
        tools,
        effectiveHistoryLength: effectiveHistory.length,
        isImageGen: tools?.includes('image_generation'),
        firstMsgRole: effectiveHistory[0]?.role,
        firstMsgContentType: typeof effectiveHistory[0]?.content
      });

      // Prepare files for data_analysis only
      let requestFiles: LegacyRequestFile[] | undefined = options?.overrideRequestFiles;
      const isDataAnalysisReq = tools?.includes('data_analysis');
  
      if (isDataAnalysisReq && !requestFiles) {
        // For data_analysis, the backend identifies files exclusively via file_ids (uploaded
        // in a prior step). Files persist in uploadedFilesState across messages until the
        // user explicitly clears them, so we always send the CURRENT set of uploaded files.
        // Only include files that have already been given a backend file_id — files that are
        // still uploading or errored are not yet ready and must not be silently excluded
        // without the user knowing. The submit guard in handleSubmit ensures at least one
        // file has a fileId before sending; here we forward exactly the ready ones.
        requestFiles = uploadedFilesState
          .filter(f => !!f.fileId)
          .map(f => ({ filename: f.name, file_id: f.fileId! } as LegacyRequestFile));
      }
      // For general chat (including PDFs): files are already in structured content format in chat history, no extraction needed
      
      // Resolve model configuration based on selected model and active tool.
      // resolveModelConfig returns null for tools that don't support model selection
      // (e.g. image_generation, data_analysis, document_translation), so the request
      // payload will omit the model field and rely on backend defaults.
      const modelConfig = resolveModelConfig(selectedModelIdRef.current, tools?.[0]);
      const requestModelId = tools?.includes('web_search')
        ? WEB_SEARCH_MODEL_ID
        : tools?.includes('data_analysis') || tools?.includes('legal_rag') || tools?.includes('rag') || tools?.includes('devops_rag') || tools?.includes('everyday_insights_rag') || tools?.includes('pa_rag')
          ? null
          : selectedModelIdRef.current;

      const handleRagSwitchSuggestion = (ragType: 'la' | 'hr') => {
        try {
          legalSwitchRequestRef.current = {
            message,
            tools,
            effectiveHistory,
            requestFiles,
            ragType
          };
        } catch {/* ignore */}

        accumulatedResponse = '';
        chunkQueue.length = 0;
        currentResponseRef.current = '';
        ragSwitchBuffer = '';
        ragSwitchDetectionActive = false;

        setState(prev => ({
          ...prev,
          isStreaming: false,
          isImageStreaming: false,
          currentResponse: '',
          currentTools: undefined,
          ragSwitchSuggestion: { message, ragType }
        }));

        try { abortControllerRef.current?.abort(); } catch {/* ignore */}
      };

      const resolveChunkForRagSwitch = (incomingContent: string): string | null => {
        if (!ragSwitchDetectionActive || !incomingContent) {
          return incomingContent;
        }

        const hasVisibleOutput =
          accumulatedResponse.length > 0 ||
          currentResponseRef.current.length > 0 ||
          chunkQueue.length > 0;

        if (hasVisibleOutput) {
          ragSwitchDetectionActive = false;
          if (ragSwitchBuffer) {
            const released = ragSwitchBuffer + incomingContent;
            ragSwitchBuffer = '';
            return released;
          }
          return incomingContent;
        }

        const candidate = ragSwitchBuffer + incomingContent;
        const normalizedCandidate = candidate.trimStart();

        const isLaPrefix = LA_SWITCH_MARKER.startsWith(normalizedCandidate);
        const isHrPrefix = HR_SWITCH_MARKER.startsWith(normalizedCandidate);

        if (isLaPrefix || isHrPrefix) {
          ragSwitchBuffer = candidate;
          if (normalizedCandidate === LA_SWITCH_MARKER) {
            handleRagSwitchSuggestion('la');
          } else if (normalizedCandidate === HR_SWITCH_MARKER) {
            handleRagSwitchSuggestion('hr');
          }
          return null;
        }

        if (normalizedCandidate.startsWith(LA_SWITCH_MARKER)) {
          const trailingContent = normalizedCandidate.slice(LA_SWITCH_MARKER.length);
          if (!trailingContent.trim()) {
            handleRagSwitchSuggestion('la');
            return null;
          }
        }

        if (normalizedCandidate.startsWith(HR_SWITCH_MARKER)) {
          const trailingContent = normalizedCandidate.slice(HR_SWITCH_MARKER.length);
          if (!trailingContent.trim()) {
            handleRagSwitchSuggestion('hr');
            return null;
          }
        }

        ragSwitchDetectionActive = false;
        if (ragSwitchBuffer) {
          const released = candidate;
          ragSwitchBuffer = '';
          return released;
        }

        return incomingContent;
      };

      await chatService.sendMessage(
        message,
        effectiveHistory,
        tools, // Pass tools to chat service
        // onChunk callback - queue chunks for delayed processing
        (content: string) => {
          logger.log('[ONCHUNK-CALLBACK][RECEIVED]', {
            contentLength: content.length,
            contentPreview: content.substring(0, 200),
            timestamp: Date.now()
          });
          
          // Queue the chunk whole - no splitting. Splitting was causing whitespace/newline
          // loss that broke markdown rendering (headers running into previous sections, etc.).
          // The backend sends properly spaced/newlined content; we just need to preserve it.
          
          const chunkTime = new Date().toISOString();
          
          // Log first content chunk
          if (!hasLoggedFirstContentRef.current) {
            logger.log('[ONCHUNK-CALLBACK][FIRST-CHUNK]', { contentLength: content.length });
            hasLoggedFirstContentRef.current = true;
          }
          
          // Always count total chunks received
          setState(prev => ({
            ...prev,
            debugInfo: {
              ...prev.debugInfo,
              firstChunkTime: prev.debugInfo.firstChunkTime || chunkTime,
              lastChunkTime: chunkTime,
              totalChunks: prev.debugInfo.totalChunks + 1
            }
          }));
          
          if (content) {
            const resolvedContent = resolveChunkForRagSwitch(content);
            if (resolvedContent === null) {
              return;
            }
            content = resolvedContent;

            // Intercept follow-up suggestions from backend (emitted as JSON with __followups flag)
            if (content.startsWith('{') && content.includes('__followups')) {
              try {
                const parsed = JSON.parse(content);
                if (parsed?.__followups === true && Array.isArray(parsed?.followups)) {
                  followUpsRef.current = parsed.followups;
                  return; // do not treat as display content
                }
              } catch { /* not valid JSON, fall through */ }
            }

            // Intercept reasoning model JSON events (__reasoning, __reasoning_final_answer)
            // Handles both GPT and Claude reasoning models (both use the 'reasoning' tool).
            // TODO: delegate to parseReasoningStream utility once extraction is complete
            if (tools?.includes('reasoning') && content.startsWith('{')) {
              try {
                const parsed = JSON.parse(content);
                if (parsed?.__reasoning === true) {
                  const piece = parsed.content || '';
                  reasoningBuffer += piece;
                  setState(prev => ({ ...prev, reasoningContent: reasoningBuffer }));
                  return; // do not treat as display content
                }
                if (parsed?.__reasoning_final_answer === true) {
                  const finalAnswer = parsed.content || '';
                  if (finalAnswer) {
                    chunkQueue.push(finalAnswer);
                    setState(prev => ({
                      ...prev,
                      debugInfo: { ...prev.debugInfo, contentChunks: prev.debugInfo.contentChunks + 1, queueLength: chunkQueue.length }
                    }));
                    scheduleProcessQueue();
                  }
                  return;
                }
                if (parsed?.__response_id) {
                  currentResponseIdRef.current = parsed.__response_id;
                  return;
                }
              } catch {/* partial/non-JSON chunk — fall through to regex guard */}
              // Discard partial JSON that still looks like a reasoning marker
              if (/"__reasoning"/.test(content)) {
                return;
              }
            }

            // Intercept web_search thinking payloads (JSON with __web_search_thinking flag)
            // Only intercept if it's explicitly marked as thinking content (not regular content from web_search)
            // Regular web_search content (non-thinking) will be plain text and will flow through to normal streaming below
            // Use the new parseWebSearchStream utility for web-search event processing
            if (tools?.includes('web_search')) {
              parseWebSearchStream(
                content,
                {
                  onResponseId: (id: string) => {
                    logger.log('[useChatStream][response-id-captured]', id);
                    currentResponseIdRef.current = id;
                  },
                  onCitations: (citations: Array<{ url: string; title: string; snippet?: string }>) => {
                    logger.log('[useChatStream][web-search-citations-captured]', { count: citations.length });
                    webSearchCitationsRef.current = citations;
                  },
                  onThinkingChunk: (piece: string) => {
                    logger.log('[useChatStream][thinking-captured-STREAMING]', { piece: piece.slice(0, 100), totalLength: piece.length });
                    setState(prev => {
                      const newThinkingContent = prev.thinkingContent ? prev.thinkingContent + '\n\n' + piece : piece;
                      logger.log('[useChatStream][thinking-state-updated-STREAMING]', {
                        previousLength: prev.thinkingContent?.length || 0,
                        newLength: newThinkingContent.length,
                        keepingCurrentResponse: prev.currentResponse.slice(0, 50)
                      });
                      return {
                        ...prev,
                        thinkingContent: newThinkingContent
                        // Keep currentResponse as-is (don't clear "Searching the web...")
                      };
                    });
                  },
                  onContentChunk: (chunk: string) => {
                    chunkQueue.push(chunk);
                    setState(prev => ({
                      ...prev,
                      debugInfo: { ...prev.debugInfo, contentChunks: prev.debugInfo.contentChunks + 1, queueLength: chunkQueue.length }
                    }));
                    scheduleProcessQueue();
                  }
                }
              );
              return;
            }
            
            // NORMALIZE & EXTRACT ANY EMBEDDED ANALYSIS EVENT JSON EVEN IF TOOLS FLAG IS MISSING
            // (Sandbox placeholder detection removed; now done post-completion.)
            const handleAnalysisEvent = (evtObj: any) => {
              if (!evtObj || !evtObj.__analysis_event) return false;
              const evtType = evtObj.eventType as string;
              const parsed = evtObj.parsed;
              let appendText = '';
              // Ensure heading is prepended at the first structural event related to outputs
              if (evtType === 'container' || evtType === 'file_citations' || evtType === 'file_citation' || evtType === 'files' || evtType === 'output_file' || evtType === 'output_files' || evtType === 'output_files_created') {
                ensureHeadingFirst();
              }
              if (evtType === 'code') {
                if (!analysisCodeBlockOpenRef.current) {
                  analysisCodeBlockOpenRef.current = true;
                  analysisCodeBufferRef.current = '';
                  // Stream the opening fence immediately
                  const openingFence = '\n```python\n';
                  chunkQueue.push(openingFence);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                }
                // Stream code content immediately instead of buffering
                const codeContent = (parsed?.data?.content ?? evtObj.raw);
                analysisCodeBufferRef.current += codeContent;
                if (codeContent) {
                  const cleaned = codeContent.replace(/\\n/g,'\n').replace(/\\t/g,'\t');
                  chunkQueue.push(cleaned);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                }
                return true; // consumed
              } else if (evtType === 'code_block_end') {
                if (analysisCodeBlockOpenRef.current) {
                  // Stream the closing fence
                  appendText += '\n```\n';
                  analysisCodeBlockOpenRef.current = false;
                  analysisCodeBufferRef.current = '';
                }
                if (appendText) {
                    // Unescape any escaped newline sequences before queueing
                    const cleaned = appendText.replace(/\\n/g,'\n').replace(/\\t/g,'\t');
                    chunkQueue.push(cleaned);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                }
                return true;
              } else if (evtType === 'text') {
                appendText += (parsed?.data?.content ?? evtObj.raw);
                // Deferred: no sandbox placeholder detection during streaming.
                if (appendText) {
                  const cleaned = appendText.replace(/\\n/g,'\n').replace(/\\t/g,'\t');
                  chunkQueue.push(cleaned);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                }
                return true;
              } else if (evtType === 'container') {
                let parsedContainerId = parsed?.data?.content?.container_id
                  || parsed?.data?.content?.container
                  || parsed?.container_id
                  || parsed?.container
                  || parsed?.data?.container_id
                  || parsed?.data?.container
                  || '';
                // Fallback: sometimes container id is a raw string in parsed.data.content
                if (!parsedContainerId) {
                  const possible = parsed?.data?.content;
                  if (typeof possible === 'string' && possible.length > 20) {
                    parsedContainerId = possible.trim();
                  }
                }
                if (!parsedContainerId) {
                  // Fallback: reuse last container or assign default
                  parsedContainerId = lastContainerIdRef.current || 'c_default';
                }
                lastContainerIdRef.current = parsedContainerId;
                if (!analysisContainersRef.current.has(parsedContainerId)) {
                  analysisContainersRef.current.set(parsedContainerId, { files: new Map() });
                }
                // If we previously emitted analysisfile links with placeholder container 'c_default', upgrade them now.
                if (parsedContainerId && parsedContainerId !== 'c_default') {
                  // Update container IDs in-place WITHOUT full content replacement
                  let replaceCount = 0;
                  const upgraded = currentResponseRef.current.replace(
                    /(\[Download[^\]]*\]\(analysisfile:)c_default(:[^)]+\))/g, 
                    (m, pre, rest) => { replaceCount++; return pre + parsedContainerId + rest; }
                  );
                  if (replaceCount > 0) {
                    currentResponseRef.current = upgraded;
                    logger.log('[container-id-upgraded]', { 
                      linksUpgraded: replaceCount, 
                      newContainerId: parsedContainerId.slice(0, 30) + '...' 
                    });
                  }
                }
                return true; // consumed, not displayed
              } else if (evtType === 'file_citations' || evtType === 'file_citation' || evtType === 'files' || evtType === 'output_file' || evtType === 'output_files' || evtType === 'output_files_created') {
                let fileEntries: any = parsed?.data?.content?.file_citations
                  || parsed?.file_citations
                  || parsed?.data?.content?.files
                  || parsed?.files
                  || parsed?.data?.file_citations
                  || parsed?.data?.files
                  || parsed?.outputs
                  || parsed?.data?.outputs
                  || parsed?.data?.content?.outputs
                  || [];
                if (!Array.isArray(fileEntries) && fileEntries) fileEntries = [fileEntries];
                const containerId = parsed?.data?.content?.container_id
                  || parsed?.data?.content?.container
                  || parsed?.container_id
                  || parsed?.container
                  || parsed?.data?.container_id
                  || parsed?.data?.container
                  || lastContainerIdRef.current
                  || 'c_default'; // c_default as fallback but will cause error
                // Inner JSON string parsing fallback (actual payload placed directly in data.content as a JSON string)
                if ((!fileEntries || fileEntries.length === 0) && (typeof parsed?.data?.content === 'string' || typeof parsed?.data?.content?.content === 'string')) {
                  const inner = (typeof parsed?.data?.content === 'string'
                    ? parsed.data.content
                    : (typeof parsed?.data?.content?.content === 'string' ? parsed.data.content.content : '')).trim();
                  if (inner && /"file_id"\s*:\s*"/.test(inner)) {
                    try {
                      const innerObj = JSON.parse(inner);
                      if (innerObj && innerObj.file_id) {
                        const displayName = (innerObj.key || innerObj.filename || innerObj.name || 'output').toString();
                        fileEntries = [{ file_id: innerObj.file_id, display_name: displayName }];
                      }
                    } catch (e) {
                      console.error('[analysis-event] file_citations inner-parse-failed', e);
                    }
                  } else if (inner) {
                    console.error('[analysis-event] file_citations inner-no-match', inner.slice(0,140));
                  }
                }
                if (Array.isArray(fileEntries) && fileEntries.length > 0) {
                  let entry = analysisContainersRef.current.get(containerId);
                  if (!entry) {
                    entry = { files: new Map() };
                    analysisContainersRef.current.set(containerId, entry);
                  }
                  const fileMap: Record<string, { fileId: string; displayName: string }> = {};
                  for (const fe of fileEntries) {
                    const fid = fe?.file_id || fe?.id;
                    const rawName = fe?.display_name || fe?.file_name || fe?.filename || fe?.name || '';
                    const base = (rawName.split(/[\\/]/).pop() || rawName || '').trim();
                    const displayName = base || `file_${entry.files.size + 1}`;
                    if (fid && !entry.files.has(fid)) {
                      entry.files.set(fid, { displayName });
                    }
                    if (fid && base && !fileMap[base]) fileMap[base] = { fileId: fid, displayName };
                    if (fid) {
                      try { logger.log('[analysis-file-registered]', JSON.stringify({ container_id: containerId, file_id: fid, display_name: displayName })); } 
                      catch {
                        logger.error('[analysis-file-registered] logging failed', { container_id: containerId, file_id: fid, display_name: displayName });
                      }
                    }
                  }
                  // Hide global blocking overlay once we have at least one file registered
                  if (entry.files.size > 0) {
                    dispatch(hideGlobalBlocking());
                  }
                  // Update file links in the current response WITHOUT full content replacement
                  // This runs in-place on currentResponseRef without triggering [__REPLACE__] duplication
                  const genericPattern = /\[Download([^\]]*)\]\(sandbox:\s*\/mnt\/data\/(.+?)\)/gi;
                  let updated = currentResponseRef.current;
                  let replaceCount = 0;
                  
                  updated = updated.replace(genericPattern, (match, frag, pathPart) => {
                    const rawPart = (pathPart || '').trim();
                    const base = rawPart.split(/[\\/]/).pop()?.trim() || rawPart;
                    const entryInfo = fileMap[base];
                    if (!entryInfo) return match; // no mapping yet
                    replaceCount++;
                    const display = entryInfo.displayName;
                    return `[Download ${display}](analysisfile:${containerId}:${entryInfo.fileId}:${encodeURIComponent(display)})`;
                  });
                  
                  // Also replace plain text sandbox links (e.g., "- **TSV:** sandbox:/mnt/data/file.tsv")
                  const plainTextPattern = /sandbox:\s*\/mnt\/data\/([^\s\n)]+)/gi;
                  updated = updated.replace(plainTextPattern, (match, pathPart) => {
                    const rawPart = (pathPart || '').trim();
                    const base = rawPart.split(/[\\/]/).pop()?.trim() || rawPart;
                    const entryInfo = fileMap[base];
                    if (!entryInfo) return match; // no mapping yet
                    replaceCount++;
                    const display = entryInfo.displayName;
                    return `[Download ${display}](analysisfile:${containerId}:${entryInfo.fileId}:${encodeURIComponent(display)})`;
                  });
                  
                  if (replaceCount > 0) {
                    // Update the ref directly - no queue, no [__REPLACE__], no duplication
                    currentResponseRef.current = updated;
                    logger.log('[file-links-updated]', { 
                      linksReplaced: replaceCount, 
                      containerId: containerId.slice(0, 30) + '...' 
                    });
                  }
                } else {
                  console.error('[analysis-event] file_citations empty', { containerId });
                }
                return true; // consumed
              } else if (evtType === 'complete') {
                // Ensure overlay is hidden at completion if still showing
                dispatch(hideGlobalBlocking());
                if (analysisCodeBlockOpenRef.current) {
                  // Stream the closing fence if code block wasn't properly closed
                  const flushText = '\n```\n';
                  analysisCodeBlockOpenRef.current = false;
                  analysisCodeBufferRef.current = '';
                  chunkQueue.push(flushText);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                }
                return true;
              } else if (evtType === 'message') {
                // Handle error messages from the API
                const errorData = parsed?.error_data || parsed?.data?.error_data;
                if (errorData?.error && errorData?.message) {
                  // Extract the user-friendly error message
                  let errorMessage = errorData.message;
                  
                  // Try to extract inner error message if it's wrapped
                  // Format: "Error code: 400 - {'error': {'message': 'Container is expired.', ...}}"
                  const innerMatch = errorMessage.match(/'message':\s*'([^']+)'/);
                  if (innerMatch && innerMatch[1]) {
                    errorMessage = innerMatch[1];
                  }
                  
                  // Format as a user-friendly error block
                  const errorText = `\n⚠️ **Error**: ${errorMessage}\n`;
                  chunkQueue.push(errorText);
                  setState(prev => ({
                    ...prev,
                    debugInfo: {
                      ...prev.debugInfo,
                      contentChunks: prev.debugInfo.contentChunks + 1,
                      queueLength: chunkQueue.length
                    }
                  }));
                  scheduleProcessQueue();
                  return true; // consumed
                }
                // If no error_data, fall through to return false
              }
              return false; // default: event NOT consumed - let caller handle it
            };

            if (content.includes('"__analysis_event"')) {
              // Split on each event boundary while preserving preceding human text (if any)
              const segments = content.split(/(?=\{"__analysis_event")/g);
              let leftoverText = '';
              logger.log('[MULTI-EVENT-PATH] Processing segments', { 
                segmentCount: segments.length,
                contentPreview: content.slice(0, 200)
              });
              for (const seg of segments) {
                if (!seg) continue;
                if (seg.startsWith('{"__analysis_event"')) {
                  try {
                    const evtObj = JSON.parse(seg);
                    const consumed = handleAnalysisEvent(evtObj);
                    logger.log('[MULTI-EVENT-PATH] Event processed', {
                      eventType: evtObj.eventType,
                      consumed,
                      segmentPreview: seg.slice(0, 100)
                    });
                    if (consumed) continue;
                    // Event NOT consumed - add to leftover for normal handling
                    leftoverText += seg;
                  } catch  {
                    logger.log('[MULTI-EVENT-PATH] Parse failed, treating as text', {
                      segmentPreview: seg.slice(0, 100)
                    });
                    leftoverText += seg; // treat as plain text if parse fails
                  }
                } else {
                  leftoverText += seg; // human text before first event
                }
              }
              logger.log('[MULTI-EVENT-PATH] Processing complete', {
                leftoverLength: leftoverText.length,
                leftoverPreview: leftoverText.slice(0, 200),
                willReturn: leftoverText.trim() === ''
              });
              if (leftoverText.trim() === '') {
                return; // everything consumed by events
              }
              // Replace content with leftover for normal handling below
              content = leftoverText;
            } else if (tools && tools.includes('data_analysis')) {
              // Original single-event fast path
              logger.log('[SINGLE-EVENT-PATH] Attempting to parse', {
                contentPreview: content.slice(0, 200),
                contentLength: content.length
              });
              try {
                const evtObj = JSON.parse(content);
                if (evtObj && evtObj.__analysis_event) {
                  const consumed = handleAnalysisEvent(evtObj);
                  logger.log('[SINGLE-EVENT-PATH] Event processed', {
                    eventType: evtObj.eventType,
                    consumed,
                    willReturn: consumed
                  });
                  if (consumed) {
                    return; // only return if event was actually consumed/handled
                  }
                  // If not consumed (e.g., unknown event type), fall through to normal handling
                  logger.log('[SINGLE-EVENT-PATH] Event not consumed, falling through to queue');
                }
              } catch (parseError) {
                logger.log('[SINGLE-EVENT-PATH] Parse failed, falling through', {
                  error: parseError instanceof Error ? parseError.message : 'unknown'
                });
                /* not event JSON */
              }
            }
            // Handle web_search start marker (don't clear currentResponse, let content replace naturally)
            if (tools && tools.includes('web_search') && content.startsWith('{')) {
              try {
                const parsed = JSON.parse(content);
                if (parsed && parsed.__web_search_start) {
                  logger.log('[useChatStream][web-search-start-marker]', {
                    thinkingComplete: true,
                    startingRegularContent: true,
                    keepingCurrentResponse: true
                  });
                  // Mark thinking as complete AND set flag that web search just completed
                  // The next content chunk will naturally replace "Searching the web..."
                  setState(prev => ({ 
                    ...prev, 
                    thinkingComplete: true, // Mark thinking as complete - this will auto-collapse the dropdown
                    webSearchJustCompleted: true // Set flag for spacing logic
                  }));
                  return; // don't queue this marker
                }
              } catch {/* not web search marker */}
            }
            // Attempt to parse JSON for uploaded_files (data analysis results)
            if (tools && tools.includes('data_analysis')) {
              try {
                const parsed = JSON.parse(content);
                if (parsed?.data?.content?.uploaded_files && Array.isArray(parsed.data.content.uploaded_files)) {
                  const results = parsed.data.content.uploaded_files.map((f: any) => ({
                    file_id: f.file_id,
                    filename: f.filename,
                    status: f.status,
                    file_type: f.file_type
                  }));
                  dispatch(setAnalysisResults(results));
                }
              } catch {/* not JSON, ignore */}
              // Deferred: no sandbox placeholder detection during streaming.
            }
            // Add content directly to queue (no artificial splitting)
            // Log regular content for debugging web_search streaming
            if (tools && tools.includes('web_search') && content.trim()) {
              logger.log('[useChatStream][REGULAR-CONTENT-STREAMING]', { 
                contentPreview: content.slice(0, 100),
                contentLength: content.length,
                isJSON: content.startsWith('{'),
                queueLength: chunkQueue.length
              });
            }
            // (Do NOT buffer generic content into reasoningBuffer; reasoning now delivered explicitly.)
            const cleanedGeneric = (tools && tools.includes('data_analysis')) ? content.replace(/\\n/g,'\n').replace(/\\t/g,'\t') : content;

            // REASONING INTERLEAVE DETECTION/REASONING STREAMING:
            // During reasoning: plain content chunks arrive one-at-a-time, each immediately
            // followed by a __reasoning chunk — they are interleave fragments, not answer content.
            // After reasoning: plain chunks arrive consecutively with no __reasoning between them.
            // Rule: hold the first plain chunk in pendingInterleaveChunk. If the next chunk is
            // __reasoning, discard the pending chunk (interleave). If the next chunk is also
            // plain, reasoning is over — flush pending + queue this one and all subsequent ones.
            // ...existing code...
            
            // CRITICAL: For image generation, check if this chunk duplicates the marker
            // The backend sends progressive updates with the full ![Generated Image](data:image/png;base64,...) each time
            // We need to avoid queueing duplicate chunks that would concatenate
            const isImageChunk = cleanedGeneric.includes('![Generated Image](data:image/png;base64,');
            if (isImageChunk) {
              // Duplicate detection: use chunk LENGTH plus a sample from the TAIL of the base64
              // (not the first 200 chars — all PNG/JPEG images share identical file header bytes,
              // so partial25, partial50, and completed all hash the same with a head-only check,
              // causing the 50% and completed images to be falsely skipped as duplicates).
              const chunkLen = cleanedGeneric.length;
              const chunkTail = cleanedGeneric.slice(-80); // tail bytes differ per quality level
              const chunkHash = `${chunkLen}:${chunkTail}`;
              
              if (chunkHash === lastImageChunkHash) {
                logger.log('[ONCHUNK-CALLBACK][IMAGE-DUPLICATE-DETECTED]', {
                  action: 'Skipping identical image chunk',
                  hashMatch: true,
                  chunkLen
                });
                // Skip this chunk entirely - it's a true duplicate
                return;
              }
              
              // Update the last hash
              lastImageChunkHash = chunkHash;
              
              // Remove any existing image chunks from the queue (they're superseded by this new one)
              const imageChunkIndices: number[] = [];
              chunkQueue.forEach((c, idx) => {
                if (c.includes('![Generated Image](data:image/png;base64,')) {
                  imageChunkIndices.push(idx);
                }
              });
              
              if (imageChunkIndices.length > 0) {
                logger.log('[ONCHUNK-CALLBACK][IMAGE-DEDUPE]', {
                  action: 'Removing old image chunks from queue',
                  oldQueueLength: chunkQueue.length,
                  removing: imageChunkIndices.length
                });
                // Remove in reverse order to maintain indices
                for (let i = imageChunkIndices.length - 1; i >= 0; i--) {
                  chunkQueue.splice(imageChunkIndices[i], 1);
                }
              }
              
              logger.log('[ONCHUNK-CALLBACK][IMAGE-CHUNK]', {
                newChunkLength: chunkLen,
                newChunkPreview: cleanedGeneric.substring(0, 200),
                queueLengthAfterClean: chunkQueue.length
              });
            }
            
            logger.log('[ONCHUNK-CALLBACK][QUEUEING-CONTENT]', {
              contentLength: cleanedGeneric.length,
              contentPreview: cleanedGeneric.slice(0, 200),
              currentQueueLength: chunkQueue.length,
              isDataAnalysis: tools?.includes('data_analysis'),
              isImageChunk
            });
            chunkQueue.push(cleanedGeneric);

            // Update content chunk count and schedule processing if not already running
            setState(prev => ({
              ...prev,
              debugInfo: {
                ...prev.debugInfo,
                contentChunks: prev.debugInfo.contentChunks + 1,
                queueLength: chunkQueue.length
              }
            }));
            scheduleProcessQueue();
          }
        },
        // onError callback
        (error: Error) => {
          setState(prev => ({
            ...prev,
            isStreaming: false,
            // Don't set error state for AbortError as it's expected when user stops stream
            error: error.name === 'AbortError' ? null : error
          }));
          // Don't call onError for AbortError as it's expected when user stops stream
          if (error.name !== 'AbortError') {
            onError?.(error);
          }
        },
        // onComplete callback
        () => {
          logger.log('[ONCOMPLETE-CALLBACK][ENTRY]', {
            completionHandledAlready: completionHandledRef.current,
            queueLength: chunkQueue.length,
            currentResponseLength: currentResponseRef.current.length,
            timestamp: Date.now()
          });
          
          // Check global completion flag first
          if (completionHandledRef.current) {
            logger.log('[ONCOMPLETE-CALLBACK][ALREADY-HANDLED]', {
              skipReason: 'completionHandledRef is true'
            });
            return;
          }
          
          // (Removed unused local forceFinalize helper; using effect + exported forceFinalize instead)

          // Drain any remaining queued chunks immediately on completion (avoid waiting loop)
          logger.log('[ONCOMPLETE-CALLBACK][DRAIN-QUEUE-START]', { queueLength: chunkQueue.length });

          // If a plain chunk was held pending interleave detection and never confirmed as
          // interleave (no __reasoning followed it), it belongs in the answer — flush it now.
          // ...existing code...
          if (chunkQueue.length > 0) {
            while (chunkQueue.length > 0) {
              const nextChunk = chunkQueue.shift()!;
              try {
                const parsedChunk = JSON.parse(nextChunk);
                if (parsedChunk.type === 'replace' && parsedChunk.content) {
                  accumulatedResponse = parsedChunk.content;
                } else {
                  accumulatedResponse += nextChunk;
                }
              } catch {
                // Same image handling logic as processQueue
                // Check if this chunk contains an image marker
                if (nextChunk.includes('![Generated Image](data:image/png;base64,')) {
                  accumulatedResponse = nextChunk;
                } else {
                  // Regular text content - accumulate
                  accumulatedResponse += nextChunk;
                }
              }
            }
            currentResponseRef.current = accumulatedResponse;
            logger.log('[ONCOMPLETE-CALLBACK][DRAIN-QUEUE-END]', {
              accumulatedLength: accumulatedResponse.length,
              refLength: currentResponseRef.current.length
            });
          }

          const waitForQueueCompletion = () => {
            // Prevent re-entry from recursive setTimeout calls
            if (isWaitingForCompletion) {
              return;
            }
            
            // Check completion flag BEFORE calling setState to avoid race condition
            // If we check inside setState, we might see stale `prev` state and overwrite the completed message
            if (completionHandledRef.current) {
              return;
            }
            
            // Mark as executing to prevent recursive calls
            isWaitingForCompletion = true;
            
            // CRITICAL: Pre-create and store the completed message FIRST, before any flags or flushSync
            // This ensures it's available for ANY batched setState calls from processQueue.
            // Use accumulatedResponse as fallback if currentResponseRef hasn't been synced yet
            // (can happen when onComplete fires before processQueue runs on mount).
            const finalContentForCompletion = currentResponseRef.current || accumulatedResponse;
            const isImageGenerationPreCheck = finalContentForCompletion.includes('![Generated Image]');
            
            if (isImageGenerationPreCheck && !imageStreamingCompleteRef.current) {
              // Pre-create the message and store in ref BEFORE setting completion flag
              // Extract reasoning and thinking here as well
              const reasoningPreExtract = reasoningBuffer.trim() || undefined;
              const thinkingPreExtract = state.thinkingContent?.trim() || undefined;
              
              completedMessageRef.current = {
                id: `${Date.now()}-${Math.random()}`,
                role: 'assistant' as const,
                content: finalContentForCompletion,
                reasoning: reasoningPreExtract,
                thinking: thinkingPreExtract
              };
              logger.log('[COMPLETION] Pre-created message BEFORE setting completion flag', {
                messageId: completedMessageRef.current.id,
                contentLength: finalContentForCompletion.length
              });
            }
            
            // CRITICAL: Don't set completionHandledRef.current before checking actual state
            // We need to check INSIDE setState to see the real current state, not stale closure state
            
            // Use flushSync to force this state update to apply immediately, preventing it from being
            // overwritten by batched setState calls from processQueue that were queued before completion
            
            flushSync(() => {
              setState(prev => {
              // If queue is empty and not processing, we can complete
              if (chunkQueue.length === 0 && !isQueueProcessing && !prev.debugInfo.isProcessingQueue) {
                // Set completion flag NOW that we've verified actual state
                completionHandledRef.current = true;
                // Reset waiting flag now that completion is successful
                isWaitingForCompletion = false;
                
                setCompletionTrigger(t => t + 1); // trigger post effects
                
                // Use the ref to get the most up-to-date content, not the potentially stale state.
                // FALLBACK: if the ref is empty (processQueue may not have synced it yet), use
                // prev.currentResponse — this covers the race where onComplete fires before
                // processQueue has had a chance to run (common when sendMessage is called from
                // a useEffect on mount, e.g. via initialMessage/initialTools from gallery tiles).
                let finalContent = finalContentForCompletion || prev.currentResponse || '';
                
                // Also sync the ref now that we have the real content
                if (finalContent && !finalContentForCompletion) {
                  currentResponseRef.current = finalContent;
                }
                
                // CRITICAL: Remove [__REPLACE__] sentinels that were added during streaming
                // These are internal markers used to trigger full content replacement in processQueue
                // and should NEVER appear in the final output
                finalContent = finalContent.replace(/\[__REPLACE__\]/g, '');
                
                // Fallback sandbox link upgrade BEFORE other mutations (attempt mapping to real file ids if discovered)
                if (tools?.includes('data_analysis')) {
                  try {
                    const sandboxRegex = /\[[^\]]*\]\(sandbox:\/mnt\/data\/([^)]+)\)/g;
                    const replacements: Array<{ original: string; filename: string; replaced?: string }> = [];
                    finalContent = finalContent.replace(sandboxRegex, (match, filePath) => {
                      const rawFilename = (filePath || '').trim().split(/[\\/]/).pop() || filePath;
                      let replaced = match; // default keep
                      // Search container maps for a matching displayName (case-insensitive)
                      for (const [contId, data] of analysisContainersRef.current.entries()) {
                        for (const [fid, meta] of data.files.entries()) {
                          if (meta.displayName.toLowerCase() === rawFilename.toLowerCase()) {
                            replaced = `[Download ${meta.displayName}](analysisfile:${contId}:${fid}:${encodeURIComponent(meta.displayName)})`;
                            break;
                          }
                        }
                        if (replaced !== match) break;
                      }
                      replacements.push({ original: match, filename: rawFilename, replaced: replaced !== match ? replaced : undefined });
                      return replaced;
                    });
                    
                    // Also handle plain text sandbox links (e.g., "- **TSV:** sandbox:/mnt/data/file.tsv")
                    const plainTextSandboxRegex = /sandbox:\s*\/mnt\/data\/([^\s\n)]+)/g;
                    finalContent = finalContent.replace(plainTextSandboxRegex, (match, filePath) => {
                      const rawFilename = (filePath || '').trim().split(/[\\/]/).pop() || filePath;
                      let replaced = match; // default keep
                      // Search container maps for a matching displayName (case-insensitive)
                      for (const [contId, data] of analysisContainersRef.current.entries()) {
                        for (const [fid, meta] of data.files.entries()) {
                          if (meta.displayName.toLowerCase() === rawFilename.toLowerCase()) {
                            replaced = `[Download ${meta.displayName}](analysisfile:${contId}:${fid}:${encodeURIComponent(meta.displayName)})`;
                            break;
                          }
                        }
                        if (replaced !== match) break;
                      }
                      replacements.push({ original: match, filename: rawFilename, replaced: replaced !== match ? replaced : undefined });
                      return replaced;
                    });
                    
                    if (replacements.some(r => r.replaced)) {
                      // Sandbox links upgraded to analysisfile links
                    }
                  } catch {/* ignore */}
                }
                // Extract reasoning now that stream is complete
                const reasoningExtract = reasoningBuffer.trim() || undefined;
                // Extract thinking content from web_search
                const thinkingExtract = prev.thinkingContent?.trim() || undefined;
                
                // For web_search: Clean up any loading message remnants and thinking tag artifacts
                if (tools?.includes('web_search')) {
                  // Remove "Searching the web..." prefix (with any number of dots and optional > characters)
                  finalContent = finalContent.replace(/^(?:Searching the web\.+|>+|Searching the web\.+>+)+/i, '').trim();
                }
                
                // For reasoning: Clean up "Thinking..." message remnants
                if (tools?.includes('reasoning')) {
                  // Remove "Thinking..." prefix (with any number of dots and optional > characters)
                  finalContent = finalContent.replace(/^(?:Thinking\.+|>+|Thinking\.+>+)+/i, '').trim();
                }
                
                // Simplified post-completion handling:
                // 1. Remove any sandbox placeholder blocks entirely.
                const sandboxBlockPattern = /(?:^|\n)\s*(?:Download(?:\s+the\s+(?:file|csv|dataset|data\s*set))?:?\s*)?(?:\n)?\[Download[^\]]*\]\(sandbox:\s*\/mnt\/data\/[^)\n]+\)\s*/gi;
                finalContent = finalContent.replace(sandboxBlockPattern, '\n');

                // 2. Append a consolidated download section if no analysisfile links yet.
                // Only treat as having links if proper markdown download link with analysisfile href exists.
                const alreadyHasAnalysisLinks = /\[Download[^\]]*\]\(analysisfile:[^)]+\)/i.test(finalContent);
                if (!alreadyHasAnalysisLinks) {
                  const lines: string[] = [];
                  for (const [contId, data] of analysisContainersRef.current.entries()) {
                    for (const [fid, meta] of data.files.entries()) {
                      const displayName = meta.displayName.trim();
                      // Force removal of any accidental spaces after scheme to ensure rehypeAnalysisfile plugin matches
                      lines.push(`[Download ${displayName}](analysisfile:${contId}:${fid}:${encodeURIComponent(displayName)})`);
                    }
                  }
                  if (lines.length > 0) {
                    finalContent = finalContent.replace(/\n{3,}/g,'\n\n').trimEnd() + `\n\n**Available Downloads:**\n` + lines.map(l => `- ${l}`).join('\n') + '\n';
                  } else {
                    // Snapshot container/file state for debugging why empty
                    const snapshot: Array<{container: string; fileCount: number}> = [];
                    for (const [contId, data] of analysisContainersRef.current.entries()) {
                      snapshot.push({ container: contId, fileCount: data.files.size });
                    }
                  }
                }
                finalContentRef.current = finalContent; // Preserve for fallback
                
                // NO DEDUPLICATION - we prevent duplicates at the source by updating links in-place
                // instead of using the [__REPLACE__] mechanism that caused triplication
                finalContentRef.current = finalContent;
                
                // Sanitize malformed analysisfile links (fix commas, stray spaces, ensure colon formatting) & log normalized lines
                if (tools?.includes('data_analysis')) {
                  try {
                    let mutated = false;
                    const originalLinks: string[] = [];
                    finalContent = finalContent.replace(/\[Download[^\]]*\]\(analysisfile[^)]*\)/gi, (m) => {
                      originalLinks.push(m);
                      let fixed = m;
                      fixed = fixed.replace(/\(analysisfile,\s*/i, '(analysisfile:'); // comma after scheme
                      fixed = fixed.replace(/\(analysisfile:\s+/i, '(analysisfile:'); // space after scheme colon
                      fixed = fixed.replace(/analysisfile:([^)]*)/i, (inner, rest) => {
                        // collapse spaces around colons in remainder
                        const cleanedRest = rest.replace(/\s*:\s*/g, ':').replace(/\s+/g, ' ').trim();
                        return 'analysisfile:' + cleanedRest;
                      });
                      // normalize parenthesis spacing
                      fixed = fixed.replace(/\s+\)$/,' )').replace(/:\s+/g, ':');
                      const linkMatch = fixed.match(/\(analysisfile:([^)]*)\)/i);
                      if (linkMatch) {
                        const raw = linkMatch[1].trim();
                        const segs = raw.split(':').map(s=>s.trim()).filter(Boolean);
                        if (segs.length >= 3) {
                          const normalized = segs[0] + ':' + segs[1] + ':' + segs.slice(2).join(':');
                          fixed = fixed.replace(/\(analysisfile:[^)]*\)/i, `(analysisfile:${normalized})`);
                        }
                      }
                      if (fixed !== m) mutated = true;
                      return fixed;
                    });
                    if (mutated) {
                      // Download links sanitized
                    }
                  } catch { /* ignore sanitize errors */ }
                }
                // Unified raw final assistant content logging (applies to all tool modes)
                try {
                  // Expose globally for quick inspection in dev tools
                  (window as any).__lastFinalAssistantRaw = finalContent;
                  // Preserve pre-render (before markdown component transforms) separately
                  (window as any).__lastFinalAssistantTimestamp = new Date().toISOString();
                  
                  // DEBUG: Print raw accumulated message to console for debugging VBA code splitting
                  // This shows the EXACT content as we received it from the backend, fully accumulated
                  // logger.log('='.repeat(80));
                  // logger.log(`RAW ACCUMULATED MESSAGE (${tools?.includes('reasoning') ? 'REASONING' : 'REGULAR'} CHAT)`);
                  // logger.log('Timestamp:', new Date().toISOString());
                  // logger.log('Tools:', tools);
                  // logger.log('Content Length:', finalContent.length);
                  // logger.log('-'.repeat(80));
                  // logger.log('RAW CONTENT:');
                  // logger.log(finalContent);
                  // logger.log('='.repeat(80));
                } catch {/* ignore */}
                
                // Only add assistant message if there's actually content
                if (!finalContent.trim()) {
                  // Stream completed with no content. Real upstream errors should have
                  // already thrown into the outer catch (with the server-provided
                  // message preserved). Reaching here means the stream closed cleanly
                  // with zero parseable content — most commonly a gateway timeout
                  // (504) on a long-running upstream call. Surface a retryable
                  // message so the assistant turn doesn't silently disappear.
                  setTimeout(() => onComplete?.(), 0);
                  const lastUserMessage = prev.chatHistory.slice().reverse().find(m => m.role === 'user');
                  const emptyResponseError = {
                    id: `${Date.now()}-${Math.random()}`,
                    role: 'assistant' as const,
                    content: '*Request failed: the server returned an empty response. Please try again.*',
                    isError: true,
                    failedUserMessage: lastUserMessage,
                  };
                  return {
                    ...prev,
                    isStreaming: false,
                    isImageStreaming: false,
                    currentResponse: '',
                    currentTools: undefined, // Clear tools when streaming completes
                    chatHistory: [...prev.chatHistory, emptyResponseError],
                  };
                }

                // Call onComplete callback only here when truly done
                setTimeout(() => onComplete?.(), 0);
                
                // Check if this was an image generation to add follow-up message
                const isImageGeneration = finalContent.includes('![Generated Image]');
                
                // Determine model and tool for the assistant message
                let model: string | null | undefined = undefined;
                let tool: string | undefined = undefined;
                if (tools?.includes('web_search')) {
                  model = getModelDisplayName(requestModelId);
                  tool = 'web_search';
                } else if (tools?.includes('reasoning')) {
                  model = getModelDisplayName(requestModelId);
                  tool = 'reasoning';
                } else if (tools?.includes('data_analysis') || tools?.includes('legal_rag') || tools?.includes('rag') || tools?.includes('devops_rag') || tools?.includes('everyday_insights_rag') || tools?.includes('pa_rag')) {
                  model = null;
                  tool = tools?.[0];
                } else if (tools?.includes('image_generation')) {
                  model = getModelDisplayName(requestModelId);
                  tool = 'image_generation';
                } else {
                  model = getModelDisplayName(requestModelId);
                  tool = 'regular_chat';
                }

                if (isImageGeneration) {
                  // CRITICAL: Prevent multiple executions of image completion logic
                  if (imageStreamingCompleteRef.current) {
                    // Check if the completed message is already in history
                    const completedMsg = completedMessageRef.current;
                    const messageInHistory = completedMsg && prev.chatHistory.find(m => m.id === completedMsg.id);
                    if (completedMsg && !messageInHistory) {
                      return {
                        ...prev,
                        isStreaming: false,
                        isImageStreaming: false,
                        currentResponse: '',
                        currentTools: undefined,
                        chatHistory: [...prev.chatHistory, { ...completedMsg, model, modelId: requestModelId, tool }]
                      };
                    }
                    return {
                      ...prev,
                      isStreaming: false,
                      isImageStreaming: false,
                      currentResponse: '',
                      currentTools: undefined
                    };
                  }
                  imageStreamingCompleteRef.current = true;
                  const completeImageMessage = completedMessageRef.current!;
                  currentResponseRef.current = '';
                  forceUpdateRef.current += 1;
                  const newState = {
                    ...prev,
                    isStreaming: false,
                    isImageStreaming: false,
                    currentResponse: '',
                    chatHistory: [
                      ...prev.chatHistory,
                      { ...completeImageMessage, model, modelId: requestModelId, tool }
                    ],
                    reasoningContent: reasoningExtract || '',
                    reasoningActive: false,
                    thinkingContent: '',
                    currentTools: undefined,
                    debugInfo: {
                      ...prev.debugInfo,
                      lastUpdate: Date.now(),
                      forceUpdate: forceUpdateRef.current
                    }
                  };
                  return newState;
                } else {
                  // Non-image generation, handle normally
                  const newMessage = { 
                    id: `${Date.now()}-${Math.random()}`,
                    role: 'assistant' as const, 
                    content: finalContent, 
                    reasoning: reasoningExtract, 
                    thinking: thinkingExtract,
                    sources: webSearchCitationsRef.current.length > 0 ? webSearchCitationsRef.current : undefined,
                    followUps: followUpsRef.current.length > 0 ? followUpsRef.current : undefined,
                    model,
                    modelId: requestModelId,
                    tool
                  };
                  const newState = {
                    ...prev,
                    isStreaming: false,
                    isImageStreaming: false,
                    chatHistory: [
                      ...prev.chatHistory,
                      newMessage
                    ],
                    currentResponse: '',
                    reasoningContent: reasoningExtract || '',
                    reasoningActive: false,
                    thinkingContent: ''
                  };
                  return newState;
                }
              } else {
                // Queue is still processing, wait a bit more (only if not already handled)
                if (!completionHandledRef.current) {
                  // DON'T reset isWaitingForCompletion - keep the guard active
                  // The next call will check completionHandledRef which processQueue will set
                  setTimeout(() => {
                    // Check again if completion happened while we were waiting
                    if (completionHandledRef.current) {
                      isWaitingForCompletion = false; // Reset now that we're done
                      return;
                    }
                    // Re-run the completion check
                    isWaitingForCompletion = false; // Allow next execution
                    waitForQueueCompletion();
                  }, 100);
                  // Also schedule a safeguard finalize if still stuck after 500ms
                  setTimeout(() => {
                    if (!completionHandledRef.current) return; // already handled
                    // We'll check streaming status in outer effect
                  }, 500);
                }
                return prev;
              }
              });
            }); // Close flushSync
          };
          
          // Start waiting for queue completion
          waitForQueueCompletion();
          // Don't call onComplete here - only call it when queue is truly done
        },
        abortControllerRef.current.signal,
        sessionId || 'pending_session',
        requestFiles,
        options?.continueLlmResponse,
        modelConfig?.modelValue,
        modelConfig?.queryType,
        options?.ragType
      );
      // Post-send housekeeping: increment sent file counts and mark conversation started
      if (!isDataAnalysis && !isImageGen && uploadedFilesState.length > 0) {
        // Increment cumulative counts for tracking
        try {
          const imageCount = uploadedFilesState.filter(f => /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)).length;
          const nonImageCount = uploadedFilesState.length - imageCount;
          dispatch(incrementSentFileCounts({ images: imageCount, nonImages: nonImageCount }));
        } catch {/* ignore */}
        // NOTE: Files are NOT cleared here - they persist in the input to show attachment status
        // Only cleared when user explicitly removes them
        dispatch(markConversationStarted());
      } else if (!chatHistoryRef.current.length) {
        dispatch(markConversationStarted());
      }
    } catch (error) {
      const errorInstance = error instanceof Error ? error : new Error('Unknown error');
      
      setState(prev => {
        const isAbort = errorInstance.name === 'AbortError' || errorInstance.message.includes('aborted');
        
        // Check if the last message is already a stopped message (user manually stopped)
        const lastMessage = prev.chatHistory[prev.chatHistory.length - 1];
        const hasStoppedMessage = lastMessage?.role === 'assistant' && (lastMessage as any).isStopped;
        
        // If user stopped the stream, don't add abort error messages, just preserve error state
        if (isAbort && hasStoppedMessage) {
          return {
            ...prev,
            isStreaming: false,
            isImageStreaming: false,
            currentTools: undefined,
            currentResponse: ''
            // Keep existing error and chatHistory as-is
          };
        }
        
        // Preserve user message for retry; append error assistant message with metadata
        const lastUserMessage = prev.chatHistory.slice().reverse().find(m => m.role === 'user');
        const adjustedHistory = prev.chatHistory; // keep history intact
        const errorAssistantMessage = !isAbort ? { role: 'assistant' as const, content: `*Request failed: ${errorInstance.message}*`, isError: true, failedUserMessage: lastUserMessage } : null;
        return {
          ...prev,
          isStreaming: false,
          isImageStreaming: false, // ensure UI unlocks image tool
          currentTools: undefined, // clear current tool selection state so user can re-select
          error: isAbort ? null : errorInstance,
          chatHistory: errorAssistantMessage ? [...adjustedHistory, errorAssistantMessage] : adjustedHistory,
          currentResponse: ''
        };
      });
      
      // Don't call onError for AbortError as it's expected when user stops stream
      if (errorInstance.name !== 'AbortError' && !errorInstance.message.includes('aborted')) {
        onError?.(errorInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isStreaming, state.debugInfo.isProcessingQueue, state.thinkingContent, getChatService, onError, onComplete, sessionId, uploadedFilesState, dispatch]);

  // Post-completion verification & fallback: ensure UI reflects completion
  // DISABLED: This fallback was causing duplicate messages because the completion handler
  // already adds the message synchronously in setState, but this timer was checking
  // before the state update propagated and adding a second message.
  // Keeping the effect shell to avoid breaking dependencies but it does nothing now.
  useEffect(() => {
    if (!completionHandledRef.current) return;
    logger.log('[POST-COMPLETION][EFFECT-TRIGGERED-BUT-DISABLED]', { 
      completionTrigger,
      reason: 'Fallback disabled to prevent duplicate messages - completion handler already adds message'
    });
    // No timers, no fallback - completion handler is sufficient
  }, [completionTrigger]);

  // Exposed manual finalize helper (can be used from ChatPage if needed)
  const forceFinalize = useCallback(() => {
    if (!completionHandledRef.current) return;
    const finalContent = finalContentRef.current || currentResponseRef.current;
    setState(prev => {
      const last = prev.chatHistory[prev.chatHistory.length - 1];
      const needsAppend = !last || last.role !== 'assistant' || last.content !== finalContent;
      // Preserve reasoning and thinking from state if creating fallback message
      const fallbackMessage = { 
        role: 'assistant' as const, 
        content: finalContent,
        ...(prev.reasoningContent && { reasoning: prev.reasoningContent }),
        ...(prev.thinkingContent && { thinking: prev.thinkingContent })
      };
      return {
        ...prev,
        isStreaming: false,
        isImageStreaming: false,
        currentResponse: '',
        chatHistory: finalContent && needsAppend ? [...prev.chatHistory, fallbackMessage] : prev.chatHistory,
        currentTools: undefined
      };
    });
  }, []);

  // Stop the current streaming request
  const stopStream = useCallback(() => {
    logger.log('[stopStream] 🛑 CALLED - Starting stop stream process');
    
    // CRITICAL: Mark completion as handled BEFORE aborting, so that any scheduled
    // waitForQueueCompletion callbacks (especially in reasoning/web_search mode) do NOT
    // fire after we've already set the stopped message, which would wipe it.
    completionHandledRef.current = true;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      logger.log('[stopStream] Aborted controller');
    } else {
      logger.log('[stopStream] No abort controller found');
    }
    
    // CRITICAL: Get the actual streamed content from the ref, not from state.
    // The ref contains the real-time accumulated content during streaming.
    let preservedContent = currentResponseRef.current || '';
    
    // If all we have is the loading placeholder (no real content yet), clear it
    // so the stopped message doesn't misleadingly show the placeholder text.
    if (preservedContent === 'Thinking...' || preservedContent === 'Searching the web...') {
      preservedContent = '';
    }
    
    logger.log('[stopStream][DEBUG]', {
      hasContent: !!preservedContent,
      contentLength: preservedContent.length,
      hasSandbox: preservedContent.includes('sandbox:'),
      hasContainer: !!lastContainerIdRef.current,
      contentPreview: preservedContent.slice(0, 200)
    });
    
    // CRITICAL: Convert any sandbox: links to analysisfile: links before finalizing
    // This handles the case where user stops the stream after download links appear
    // but before the normal postProcessAnalysisLinks would run (which only happens after streaming completes)
    if (preservedContent.includes('sandbox:')) {
      logger.log('[stopStream] Found sandbox links, attempting conversion');
      
      if (!lastContainerIdRef.current) {
        console.warn('[stopStream] No container ID available yet');
      } else {
        const containerId = lastContainerIdRef.current;
        const containerEntry = analysisContainersRef.current.get(containerId);
        
        logger.log('[stopStream][DEBUG] Container info:', {
          containerId: containerId.slice(0, 30) + '...',
          hasEntry: !!containerEntry,
          filesCount: containerEntry?.files.size || 0
        });
        
        if (containerEntry && containerEntry.files.size > 0) {
          // Build a map of filename -> fileId for quick lookup
          const fileMap: Record<string, { fileId: string; displayName: string }> = {};
          containerEntry.files.forEach((fileInfo, fileId) => {
            const base = fileInfo.displayName.split(/[\\/]/).pop()?.trim() || fileInfo.displayName;
            if (!fileMap[base]) {
              fileMap[base] = { fileId, displayName: fileInfo.displayName };
            }
          });
          
          logger.log('[stopStream][DEBUG] File map:', Object.keys(fileMap));
          
          // Convert sandbox: links to analysisfile: links using the same pattern as normal streaming
          const sandboxPattern = /\[Download([^\]]*)\]\(sandbox:\s*\/mnt\/data\/(.+?)\)/gi;
          let replaceCount = 0;
          
          preservedContent = preservedContent.replace(sandboxPattern, (match, frag, pathPart) => {
            logger.log('[stopStream][DEBUG] Found sandbox link:', { match, frag, pathPart });
            const rawPart = (pathPart || '').trim();
            const base = rawPart.split(/[\\/]/).pop()?.trim() || rawPart;
            const entryInfo = fileMap[base];
            logger.log('[stopStream][DEBUG] Looking up:', { base, found: !!entryInfo });
            if (!entryInfo) {
              console.warn('[stopStream] No file mapping found for:', base);
              return match; // Keep original if no mapping
            }
            replaceCount++;
            const display = entryInfo.displayName;
            const converted = `[Download ${display}](analysisfile:${containerId}:${entryInfo.fileId}:${encodeURIComponent(display)})`;
            logger.log('[stopStream][DEBUG] Converted:', { from: match, to: converted });
            return converted;
          });
          
          if (replaceCount > 0) {
            logger.log('[stopStream] ✅ Converted sandbox links:', { 
              linksReplaced: replaceCount,
              containerId: containerId.slice(0, 30) + '...'
            });
          } else {
            console.warn('[stopStream] ⚠️ No sandbox links were replaced despite pattern matching');
          }
        } else if (containerEntry) {
          // FALLBACK: Files haven't been registered yet
          // Instead of trying to convert with fake file_ids, keep sandbox links but mark message
          // so the UI can disable them with a helpful message
          console.warn('[stopStream] ⚠️ No file mappings available yet - files not ready for download');
          logger.log('[stopStream] Sandbox links will be kept disabled. User should retry or wait for completion.');
        } else {
          console.warn('[stopStream] No file mappings available, sandbox links will remain unclickable');
        }
      }
    } else {
      logger.log('[stopStream] No sandbox links found in content');
    }
    
    // CRITICAL: Close any unclosed code blocks before finalizing
    logger.log('[stopStream] Checking for unclosed code blocks:', {
      analysisCodeBlockOpen: analysisCodeBlockOpenRef.current,
      contentLength: preservedContent.length,
      contentPreview: preservedContent.substring(0, 200)
    });
    
    // Use the ref that tracks code block state during streaming
    if (analysisCodeBlockOpenRef.current) {
      logger.log('[stopStream] Detected unclosed code block (analysisCodeBlockOpenRef.current=true), adding closing fence');
      preservedContent += '\n```\n';
      analysisCodeBlockOpenRef.current = false;
    } else {
      // Fallback: Count opening ``` vs closing ``` to detect unclosed blocks
      const codeBlockMatches = preservedContent.match(/```/g);
      const codeBlockCount = codeBlockMatches ? codeBlockMatches.length : 0;
      logger.log('[stopStream] Code block count check:', { count: codeBlockCount, isOdd: codeBlockCount % 2 !== 0 });
      if (codeBlockCount % 2 !== 0) {
        // Odd number means there's an unclosed code block
        logger.log('[stopStream] Detected unclosed code block via counting, adding closing fence');
        preservedContent += '\n```\n';
      }
    }
    
    const stoppedMessage = preservedContent 
      ? preservedContent + '\n\n*[Response was stopped by user.]*'
      : '*[Response was stopped by user.]*';
    
    logger.log('[stopStream][FINAL]', {
      messageLength: stoppedMessage.length,
      hasSandbox: stoppedMessage.includes('sandbox:'),
      hasAnalysisfile: stoppedMessage.includes('analysisfile:'),
      preview: stoppedMessage.slice(0, 300)
    });
    
    setState(prev => {
      // Find the last user message to store as context for retry
      const lastUserMessage = [...prev.chatHistory].reverse().find(m => m.role === 'user');
      
      return {
        ...prev,
        isStreaming: false,
        currentResponse: '', // Clear current response since we're moving it to chat history
        chatHistory: [...prev.chatHistory, {
          id: currentResponseIdRef.current || undefined, // Use captured backend response ID if available
          role: 'assistant',
          content: stoppedMessage,
          isStopped: true,
          failedUserMessage: lastUserMessage
        }],
        // Preserve existing error state when stopping (don't clear it)
        // error: prev.error (implicitly preserved by spread)
      };
    });
  }, []);

  // Clear chat history
  const clearChat = useCallback(() => {
    if (state.isStreaming) {
      stopStream();
    }
    
    // Clear both state and ref
    chatHistoryRef.current = [];
    // Also clear completion-related refs to prevent stale message reuse
    completedMessageRef.current = null;
    completionHandledRef.current = false;
    finalContentRef.current = '';
    currentResponseRef.current = '';
    
    setState(prev => ({
      ...prev,
      chatHistory: [],
      currentResponse: '',
      error: null
    }));
    
    // Also clear any uploaded files attached to the previous conversation
    dispatch(clearUploadedFiles());
    // Clear analysis results (file download links from data analysis)
    dispatch(clearAnalysisResults());
    // Clear the analysis containers ref to prevent "Available Downloads" from persisting
    analysisContainersRef.current.clear();
    // Do NOT reset session id; it is per window instance
  }, [state.isStreaming, stopStream, dispatch]);

  // Session id persisted via Redux; no local ref initialization here.

  // Add a message to chat history (useful for system messages)
  const addMessage = useCallback((message: ChatMessage) => {
    setState(prev => ({
      ...prev,
      chatHistory: [...prev.chatHistory, message]
    }));
  }, []);

  // Remove stopped messages from chat history (useful before retry)
  const removeStoppedMessages = useCallback(() => {
    setState(prev => ({
      ...prev,
      chatHistory: prev.chatHistory.filter(m => !(m as any).isStopped)
    }));
  }, []);

  // Remove the last message from chat history (useful for replacing placeholder messages)
  const removeLastMessage = useCallback(() => {
    setState(prev => ({
      ...prev,
      chatHistory: prev.chatHistory.slice(0, -1)
    }));
  }, []);

  const clearRagSwitchSuggestion = useCallback(() => {
    legalSwitchRequestRef.current = null;
    if (state.isStreaming) {
      stopStream();
    }
    setState(prev => ({
      ...prev,
      ragSwitchSuggestion: null,
      chatHistory:[]
    }));
  }, [state.isStreaming, stopStream]);

  const continueAfterRagSwitchDeclined = useCallback(async () => {
    const pending = legalSwitchRequestRef.current;
    if (!pending) return;

    // Clear the dialog state before retrying.
    legalSwitchRequestRef.current = null;
    setState(prev => ({ ...prev, ragSwitchSuggestion: null }));

    await sendMessage(pending.message, pending.tools, {
      overrideHistory: pending.effectiveHistory,
      overrideRequestFiles: pending.requestFiles,
      continueLlmResponse: true,
      detectRagSwitch: false,
      ragType: pending.ragType
    });
  }, [sendMessage]);

  /**
   * Explicit user-click retry. On every retry we immediately truncate the history sent to
   * the API to at most the last two real messages — the most recent non-error assistant
   * response (if one exists) followed by the failed user message. This keeps enough context
   * for the model to give a coherent answer while drastically reducing payload size, which is
   * the most common cause of 502s on long conversations.
   *
   * The chat display is unaffected because overrideHistory bypasses the setState that would
   * otherwise append a duplicate user bubble.
   */
  const retryMessage = useCallback(async (
    message: string,
    tools?: string[],
    failedUserMessage?: ChatMessage
  ) => {
    logger.log('[retryMessage]', { hasFailedMsg: !!failedUserMessage });

    if (failedUserMessage) {
      const history = chatHistoryRef.current;

      // Only look for a prior assistant message if there are at least 2 messages in history.
      // Guards against an out-of-bounds / unexpected result on a single-message conversation.
      const lastAssistant = history.length > 1
        ? [...history].reverse().find(m => m.role === 'assistant' && !(m as any).isError)
        : undefined;

      const truncatedHistory: ChatMessage[] = lastAssistant
        ? [lastAssistant, failedUserMessage]
        : [failedUserMessage];

      logger.log('[retryMessage] truncating history', {
        truncatedLength: truncatedHistory.length,
        hadPriorAssistant: !!lastAssistant,
      });

      await sendMessage(message, tools, { overrideHistory: truncatedHistory });
    } else {
      // No failed message reference available — fall back to a normal send
      await sendMessage(message, tools);
    }
  }, [sendMessage]);

  // Debug: Log the actual state before returning
  useEffect(() => {
    const lastMessage = state.chatHistory[state.chatHistory.length - 1];
    if (lastMessage?.role === 'assistant') {
      logger.log('[useChatStream][returning-state]', {
        messageCount: state.chatHistory.length,
        lastMessageKeys: Object.keys(lastMessage),
        lastMessageThinking: lastMessage.thinking,
        lastMessageHasThinking: 'thinking' in lastMessage
      });
    }
  }, [state.chatHistory]);

  // Safety timeout: force-complete if the stream goes IDLE for longer than the tool-specific
  // window. The timer is rescheduled on every processed chunk (via lastChunkTimeRef), so a
  // healthy long-running stream that keeps emitting data will never be cancelled.
  // Only a genuinely stalled connection — where no chunk arrives within idleMs — trips the timeout.
  useEffect(() => {
    if (!state.isStreaming) return;
    const activeTools = state.currentTools;
    const isLongRunning =
      activeTools?.includes('image_generation') ||
      activeTools?.includes('data_analysis') ||
      activeTools?.includes('reasoning'); // Opus extended-thinking can exceed 2 min
    const isExtended =
      activeTools?.includes('web_search') ||
      activeTools?.includes('legal_rag') ||
      activeTools?.includes('rag') ||
      activeTools?.includes('devops_rag') ||
      activeTools?.includes('everyday_insights_rag') ||
      activeTools?.includes('pa_rag');
    const hasFiles = requestHasFilesRef.current;
    // Idle window: how long silence is tolerated before declaring the stream stalled.
    const idleMs = isLongRunning ? 210_000 : isExtended ? 120_000 : hasFiles ? 180_000 : 90_000; // 3.5 min (reasoning/image/analysis) / 2 min / 3 min (files) / 1.5 min

    let timer: ReturnType<typeof setTimeout>;
    const scheduleIdleCheck = () => {
      timer = setTimeout(() => {
        const msSinceLastChunkNow = Date.now() - lastChunkTimeRef.current;
        if (msSinceLastChunkNow < idleMs) {
          // A chunk arrived since we scheduled this check — reschedule for the remainder.
          scheduleIdleCheck();
          return;
        }
        // No chunk for idleMs — treat as stalled and force-complete.
      // Stop the in-flight request so the server connection is released
      // and onChunk stops receiving data.
      try { abortControllerRef.current?.abort(); } catch {/* ignore */}
      abortControllerRef.current = null;
      // Prevent processQueue / scheduleProcessQueue from updating the UI
      // after we've already force-completed the state below.
      completionHandledRef.current = true;
      setState(prev => {
        if (!prev.isStreaming) return prev;
        const finalContent = currentResponseRef.current?.trim();
        const lastUserMessage = prev.chatHistory.slice().reverse().find(m => m.role === 'user');
        const timeoutMessage = {
          id: `${Date.now()}-${Math.random()}`,
          role: 'assistant' as const,
          content: '*Request timed out. Please try again.*',
          isError: true,
          failedUserMessage: lastUserMessage,
        };
        return {
          ...prev,
          isStreaming: false,
          isImageStreaming: false,
          currentResponse: '',
          currentTools: undefined,
          chatHistory: finalContent
? [...prev.chatHistory, { id: `${Date.now()}-${Math.random()}`, role: 'assistant' as const, content: finalContent + '\n\n*[Response was cut off due to a connection timeout.]*' }]            : [...prev.chatHistory, timeoutMessage],
        };
      });
        dispatch(setIsStreaming(false));
      }, idleMs);
    };
    // Stamp the start time so a stream that produces its first chunk after several
    // seconds doesn't immediately trip the idle check.
    lastChunkTimeRef.current = Date.now();
    scheduleIdleCheck();
    return () => clearTimeout(timer);
  }, [state.isStreaming, state.currentTools, dispatch]);

  return {
    // State
    isStreaming: state.isStreaming,
    isImageStreaming: state.isImageStreaming,
    error: state.error,
    currentResponse: state.currentResponse,
    chatHistory: state.chatHistory,
    currentTools: state.currentTools,
    reasoningContent: state.reasoningContent,
    reasoningActive: state.reasoningActive,
    thinkingContent: state.thinkingContent,
    thinkingComplete: state.thinkingComplete,
    ragSwitchSuggestion: state.ragSwitchSuggestion,
    debugInfo: state.debugInfo,
    
    // Actions
    sendMessage,
    retryMessage,
    stopStream,
    clearChat,
    addMessage,
    removeStoppedMessages,
    removeLastMessage
    , forceFinalize
    , clearRagSwitchSuggestion
    , continueAfterRagSwitchDeclined
  };
};